<template>
    <div v-if="chatUserLoaded() && conversationsLoaded && talkjsLoaded">
        <div
            v-b-tooltip.hover.left
            :title="$t('shared.chat.schoolSpecificChats')"
            class="btn-secondary btn clickable add-btn"
            style="background-color: white !important"
            @click="toggleNewChat"
        >
            <div
                class="chat-icon-wrapper d-flex align-items-center justify-content-center h-100 w-100"
            >
                <font-awesome-icon :icon="['fas', 'comments']" size="xl" />
            </div>
        </div>

        <ActiveChats
            v-if="conversationsLoaded"
            :conversations="conversations"
            :unreads="unreads"
            @active-conversation="onActiveConversationChange"
            @remove-conversation="onConversationRemove"
        />

        <Inbox
            v-if="activeConversation !== null"
            :conversation-id="activeConversation ? activeConversation.id : null"
            :active-conversation="activeConversation"
            :session="session"
            @minimize="() => onActiveConversationChange(null)"
            @close="onConversationRemove"
        />

        <NewChat
            v-if="showNewChat && schoolId !== null"
            :school-id="schoolId"
            @close="closeNewChat"
        />
    </div>
</template>

<script>
import Talk from 'talkjs';
import UserConsentContext, { CONSENT_OPTIONS } from '~/services/UserConsentContext';

// Services
import MessageService from '~/services/MessageService';
import DialogService from '~/services/DialogService';
import TalkJSService from '~/services/TalkJSService';

// Components
import ActiveChats from './ActiveChats/ActiveChats';
import Inbox from './Inbox';
import NewChat from './NewChat';
import { CandidateStudyProgramHelper } from '~/utils/candidate/CandidateStudyProgramHelper';

export default {
    name: 'Chat',
    components: { NewChat, Inbox, ActiveChats },
    data() {
        return {
            activeConversation: null,
            unreads: {},
            session: {},
            talkjsLoaded: false,
            conversations: [],
            allConversations: [],
            conversationsLastUnread: {},
            conversationsLoaded: false,
            schoolConfig: { loading: true },
            showNewChat: false,
        };
    },
    computed: {
        chatUser() {
            return this.$interfaces.communication.chat.user.me.get({
                reactive: true,
            });
        },
        studyPrograms() {
            return this.$store.getters['profileModule/getStudyPrograms']();
        },
        schoolId() {
            return CandidateStudyProgramHelper.calculateSchoolIdOfMostRelevance(this.studyPrograms);
        },
    },
    methods: {
        chatUserLoaded() {
            return !this.chatUser.loading;
        },
        onActiveConversationChange(value) {
            if (value) {
                this.closeNewChat();
                if (this.activeConversation) this.conversations.unshift(this.activeConversation);
                if (this.conversations.filter(item => item.id === value).length > 0) {
                    const currentIndex = this.conversations.findIndex(item => item.id === value);
                    this.activeConversation = this.conversations[currentIndex];
                    this.conversations.splice(currentIndex, 1);
                }
            } else {
                if (this.activeConversation) {
                    this.conversations.unshift(this.activeConversation);
                }
                this.activeConversation = value;
            }
        },
        onConversationRemove(conversationId) {
            if (conversationId) {
                if (
                    this.conversations.filter(item => Boolean(item) && item.id === conversationId)
                        .length > 0
                ) {
                    const currentIndex = this.conversations.findIndex(
                        item => Boolean(item) && item.id === conversationId
                    );
                    this.conversations.splice(currentIndex, 1);
                } else if (
                    Boolean(this.activeConversation) &&
                    this.activeConversation.id === conversationId
                ) {
                    this.activeConversation = null;
                }
                this.$interfaces.communication.chat.conversation.id(conversationId).put({
                    body: {
                        open: false,
                    },
                });
                const allIndex = this.allConversations.findIndex(
                    item => Boolean(item) && item.id === conversationId
                );
                if (allIndex >= 0) {
                    this.allConversations[allIndex].open = false;
                }
            }
        },
        putConversationFirst(conversationId) {
            if (this.conversations.filter(item => item.id === conversationId).length > 0) {
                const currentIndex = this.conversations.findIndex(
                    item => item.id === conversationId
                );
                if (currentIndex > 0) {
                    const item = this.conversations[currentIndex];
                    this.conversations.splice(currentIndex, 1);
                    this.conversations.unshift(item);
                }
            }
        },
        toggleNewChat() {
            if (this.showNewChat) {
                this.showNewChat = false;
            } else {
                this.showNewChat = true;
                this.onActiveConversationChange(null);
            }
        },
        closeNewChat() {
            this.showNewChat = false;
        },
        getExistingConversationData(chatUserId, convId = null) {
            let res = null;
            let conversations = this.allConversations.filter(conv => {
                if (conv) {
                    let filteredParticipants = conv.participants.filter(participant => {
                        return participant.id === chatUserId;
                    });
                    return !!(filteredParticipants && filteredParticipants.length);
                }
            });

            if (convId) conversations = this.allConversations.filter(conv => conv.id === convId);
            if (conversations && conversations.length) res = conversations[0];

            return res;
        },
        onMessageReceived(message) {
            // Deal with messages with custom data, which should trigger custom events
            if (message.custom && message.custom.events) {
                const events = JSON.parse(message.custom.events);
                TalkJSService.handleEvents(this.session, this.chatUser.data, message, events, process.env);
            }
        },
    },
    async mounted() {
        const schoolId = this.schoolId;

        this.$interfaces.communication.chat.user.me
            .get({
                school_id: schoolId,
                analytics_consent: UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS),
            })

            .then(resOuter => {
                this.$interfaces.communication.chat.conversation.get().then(res => {
                    this.allConversations = res.data;
                    this.conversations = res.data.filter(item => item.open);
                    this.conversationsLoaded = true;
                    Talk.ready.then(() => {
                        let u = new Talk.User(resOuter.data.id);
                        this.session = new Talk.Session({
                            appId: process.env.VUE_APP_TALKJS_APPID,
                            me: u,
                            signature: resOuter.data.signature,
                        });
                        this.talkjsLoaded = true;

                        this.session.unreads.on('change', e => {
                            if (e && e.length) {
                                for (let i = 0; i < e.length; i++) {
                                    if (!e[i].lastMessage.sender) continue;
                                    let id = e[i].lastMessage.conversation.id;
                                    let ts = e[i].lastMessage.timestamp;
                                    if (this.conversationsLastUnread[id] !== ts) {
                                        this.conversationsLastUnread[id] = ts;
                                        this.putConversationFirst(id);
                                    }

                                    let match =
                                        this.activeConversation &&
                                        id === this.activeConversation.id;
                                    for (let j = 0; j < this.conversations.length; j++) {
                                        if (
                                            e[i].lastMessage.conversation.id ===
                                            this.conversations[j].id
                                        ) {
                                            match = true;
                                        }
                                    }
                                    if (!match) {
                                        this.$root.$emit(
                                            'chat-conversation-create-update',
                                            e[i].lastMessage.sender.id,
                                            false,
                                            e[i].lastMessage.conversation.id
                                        );
                                    }
                                }
                            }
                            this.unreads = e;
                        });

                        // Video Chat logic

                        if (localStorage.getItem('wb_current_video_chat')) {
                            DialogService.openVideoChatDialogInvitation({
                                videoChatAction: 're-invited',
                                videoChatID: localStorage.getItem('wb_current_video_chat'),
                            });
                        }
                        this.session.on('message', this.onMessageReceived);
                        this.session.on('message', message => {
                            if (message) {
                                if (
                                    message.custom &&
                                    message.custom &&
                                    message.custom.videoChatAction &&
                                    message.custom.videoChatAction === 'invited'
                                ) {
                                    let filtered = this.allConversations.filter(
                                        item => item.id === message.conversation.id
                                    );
                                    let conversation = filtered[0] || {};
                                    let participants = conversation.participants || [];
                                    let participant = participants[0];
                                    let participantName = participant.displayName || 'Unknown';
                                    let participantAvatar = participant.avatar || null;

                                    let dataObject = {
                                        ...message.custom,
                                        fromName: participantName,
                                        fromAvatar: participantAvatar,
                                    };
                                    if (localStorage.getItem('wb_current_video_chat')) {
                                        this.$root.$emit('video-chat-new-invite', dataObject);
                                        this.$root.$emit(
                                            'video-chat-new-invite-invitation',
                                            dataObject
                                        );
                                    } else {
                                        DialogService.openVideoChatDialogInvitation(dataObject);
                                    }
                                }
                            }
                        });
                    });
                });
            });

        // Can be called from any component. Used to start a new chat, will handle existing closed/open chat and new.
        this.$root.$on(
            'chat-conversation-create-update',
            (participantId, open = true, convId = null) => {
                if (!this.conversationsLoaded) {
                    MessageService.error(
                        'Chat error',
                        'Unable to create chat, conversations not loaded'
                    );
                    return;
                }
                let convData = this.getExistingConversationData(participantId);
                if (convId) {
                    convData = this.getExistingConversationData(participantId, convId);
                    if (!convData) {
                        console.log(
                            'DevError: Received an unread chat to an unknown conversation. Refresh the site.'
                        );
                        return;
                    }
                }
                if (convData) {
                    if (convData.open) {
                        open && this.onActiveConversationChange(convData.id);
                    } else {
                        convData.open = true;
                        this.$interfaces.communication.chat.conversation.id(convData.id).put({
                            body: {
                                open: true,
                            },
                        });
                        this.conversations.push(convData);
                        open && this.onActiveConversationChange(convData.id);
                    }
                } else {
                    this.$interfaces.communication.chat.conversation
                        .post({
                            body: {
                                participants: [participantId],
                            },
                        })
                        .then(res => {
                            this.conversations.push(res);
                            this.allConversations.push(res);
                            open && this.onActiveConversationChange(res.id);
                        })
                        .catch(err => {
                            if (err.response && err.response.status === 400) {
                                if (
                                    err.response.data &&
                                    err.response.data.length &&
                                    err.response.data[0].description
                                ) {
                                    MessageService.error(
                                        'Chat error',
                                        err.response.data[0].description
                                    );
                                }
                            }
                            if (err.response && err.response.status >= 500) {
                                MessageService.error(
                                    'Chat error',
                                    'An unknown error occurred. Try again later or contact support.'
                                );
                            }
                        });
                }
            }
        );
    },
    beforeDestroy() {
        this.session.off('message', this.onMessageReceived);
    },
};
</script>

<style lang="scss" scoped>
.add-btn {
    height: 60px;
    width: 60px;
    border-radius: 50%;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 3px rgba(0, 0, 0, 0.11);
    position: fixed;
    right: 85px;
    bottom: 20px;
    background-color: transparent !important;
    border-color: #3f51b5 !important;
}
.add-btn:hover {
    background-color: rgba(0, 0, 0, 0.1);
}
.chat-icon-wrapper {
    color: #252f69;
}
</style>
